import React, { useState } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text } from "@xsolla/xui-primitives";
import { useResolvedTheme, useId } from "@xsolla/xui-core";
import type { CheckboxTagGroupProps } from "./types";

// Shape of the design-system control palette tokens (mirrors the Button
// component). The JSON tokens carry more fields than the public theme type
// exposes, so `theme.colors.control` is read via a narrow assertion below.
interface ControlVariantStyles {
  bg: string;
  bgHover: string;
  bgPress: string;
  bgDisable?: string;
  border: string;
  borderHover: string;
  borderPress: string;
  borderDisable?: string;
}

interface ControlTextStyles {
  primary: string;
  secondary: string;
  tertiary: string;
  ghost: string;
  disable: string;
}

export const CheckboxTagGroup: React.FC<CheckboxTagGroupProps> = ({
  options,
  value,
  defaultValue = [],
  onChange,
  size = "md",
  variant = "secondary",
  tone = "brand",
  selectionMode = "multiple",
  disabled = false,
  errorMessage,
  "aria-label": ariaLabel,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const sizeConfig = theme.sizing.checkboxTagGroup(size);
  const fieldGap = theme.sizing.input(size).fieldGap;
  const sizeStyles = theme.sizing.input(size);
  const errorId = useId();

  const [selectedValues, setSelectedValues] = useState<string[]>(defaultValue);
  const currentValues = value !== undefined ? value : selectedValues;
  const hasError = Boolean(errorMessage);

  // Resolve the design-system control palette for the chosen tone. The
  // `primary` variant fills selected tags with these tone-colored tokens; the
  // `secondary` variant keeps the original low-emphasis monotone look and
  // ignores `tone`. Guarded with legacy fallbacks so a missing tone degrades
  // gracefully instead of throwing (mirrors Button's defensive resolution).
  const controlTone = theme?.colors?.control?.[tone] as unknown as
    | (Record<string, ControlVariantStyles> & { text: ControlTextStyles })
    | undefined;
  const controlPrimary = controlTone?.primary;
  const controlPrimaryText = controlTone?.text?.primary;
  const isPrimary = variant === "primary";

  // Selected-tag appearance. `secondary` => legacy monotone tokens (unchanged).
  // `primary` => solid, tone-colored fill from the control palette.
  const activeBg = isPrimary
    ? (controlPrimary?.bg ?? theme.colors.background.primary)
    : theme.colors.background.primary;
  const activeBorder = isPrimary
    ? (controlPrimary?.border ?? theme.colors.border.secondary)
    : theme.colors.border.secondary;
  const activeText = isPrimary
    ? (controlPrimaryText ?? theme.colors.content.primary)
    : theme.colors.content.primary;
  const activeHoverStyle =
    isPrimary && controlPrimary
      ? {
          backgroundColor: controlPrimary.bgHover ?? activeBg,
          borderColor: controlPrimary.borderHover ?? activeBorder,
        }
      : undefined;

  const isSingle = selectionMode === "single";
  const groupRole = isSingle ? "radiogroup" : "group";
  const itemRole = isSingle ? "radio" : "checkbox";

  const handleToggle = (optionValue: string) => {
    if (disabled) return;

    const isSelected = currentValues.includes(optionValue);
    let newValues: string[];
    if (isSingle) {
      // Single mode: selecting a new tag replaces the current selection;
      // clicking the active tag clears it (toggle off).
      newValues = isSelected ? [] : [optionValue];
    } else {
      newValues = isSelected
        ? currentValues.filter((v) => v !== optionValue)
        : [...currentValues, optionValue];
    }

    if (value === undefined) {
      setSelectedValues(newValues);
    }

    onChange?.(newValues);
  };

  return (
    <Box flexDirection="column" gap={fieldGap}>
      <Box
        role={groupRole}
        aria-label={ariaLabel}
        flexDirection="row"
        flexWrap="wrap"
        alignItems="center"
        gap={sizeConfig.gap}
      >
        {options.map((option) => {
          const isActive = currentValues.includes(option.value);
          const isDisabled = disabled || option.disabled;

          const bgColor = isDisabled
            ? "transparent"
            : isActive
              ? activeBg
              : theme.colors.overlay.mono;

          const borderColor = isDisabled
            ? "transparent"
            : isActive
              ? activeBorder
              : theme.colors.border.secondary;

          const textColor = isDisabled
            ? theme.colors.content.tertiary
            : isActive
              ? activeText
              : theme.colors.content.primary;

          return (
            <Box
              testID={testID}
              key={option.value}
              as="button"
              role={itemRole}
              aria-checked={isActive}
              aria-disabled={isDisabled}
              aria-describedby={hasError ? errorId : undefined}
              tabIndex={isDisabled ? -1 : 0}
              onPress={() => !isDisabled && handleToggle(option.value)}
              backgroundColor={bgColor}
              borderWidth={1}
              borderColor={borderColor}
              borderStyle="solid"
              borderRadius={sizeConfig.borderRadius}
              paddingHorizontal={sizeConfig.paddingHorizontal}
              paddingVertical={sizeConfig.paddingVertical}
              flexDirection="row"
              alignItems="center"
              justifyContent="center"
              gap={sizeConfig.gap}
              hoverStyle={
                isDisabled
                  ? undefined
                  : isActive
                    ? activeHoverStyle
                    : {
                        backgroundColor: theme.colors.overlay.mono,
                        borderColor: "transparent",
                      }
              }
              style={{
                cursor: isDisabled ? "not-allowed" : "pointer",
                flexShrink: 0,
              }}
            >
              <Text
                color={textColor}
                fontSize={sizeConfig.fontSize}
                fontWeight="400"
                textAlign="center"
                style={{
                  lineHeight: `${sizeConfig.lineHeight}px`,
                }}
              >
                {option.label}
              </Text>
            </Box>
          );
        })}
      </Box>

      {errorMessage && (
        <Text
          id={errorId}
          role="alert"
          color={theme.colors.content.alert.primary}
          fontSize={sizeConfig.fontSize - 2}
          style={{ lineHeight: sizeStyles.lineHeight + "px" }}
        >
          {errorMessage}
        </Text>
      )}
    </Box>
  );
};

CheckboxTagGroup.displayName = "CheckboxTagGroup";
